import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { BehaviorSubject, combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith, switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';

const MOBILE_BREAK_WIDTH: number = 995;

@Injectable({providedIn: 'root'})
export class SidebarService implements OnDestroy {
  private disabled$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public disabled$: Observable<boolean> = this.disabled$$.asObservable();

  private static$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public static$: Observable<boolean>;

  private forceShowMenuToggle$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public forceShowMenuToggle$: Observable<boolean> = this.forceShowMenuToggle$$.asObservable();

  private open$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public open$: Observable<boolean>;

  public showMenuToggle$: Observable<boolean>;

  public mobile$: Observable<boolean>;

  private _destroyed$$: Subject<void> = new Subject<void>();

  constructor(private router: Router) {
    this.mobile$ = fromEvent(window, 'resize').pipe(
      map((event: any) => event.srcElement.innerWidth),
      startWith(window.innerWidth),
      map((width: number) => width < MOBILE_BREAK_WIDTH),
      distinctUntilChanged(),
      tap(() => {
        if (this.static$$.getValue()) {
          this.setOpen(false);
        }
      }),
      shareReplay(1)
    );

    this.open$ = combineLatest([this.open$$.asObservable(), this.disabled$, this.static$$.asObservable(), this.mobile$]).pipe(
      map(([isOpen, isDisabled, isStatic, isMobile]: [boolean, boolean, boolean, boolean]) => (isDisabled) ? false : (isStatic && !isMobile) ? !isOpen : isOpen),
      shareReplay(1)
    );

    this.static$ = combineLatest(this.static$$.asObservable(), this.mobile$, this.disabled$, this.open$$.asObservable()).pipe(
      map(([isStatic, isMobile, isDisabled, isOpen]: [boolean, boolean, boolean, boolean]) => (isDisabled) ? false : (isMobile) ? false : (isStatic) ? !isOpen : false),
      shareReplay(1)
    );

    this.showMenuToggle$ = combineLatest([this.static$, this.disabled$, this.forceShowMenuToggle$]).pipe(
      map(([isStatic, isDisabled, forceShowToggle]: [boolean, boolean, boolean]) => (isDisabled) ? false : (forceShowToggle) ? true : !isStatic),
      distinctUntilChanged(),
      shareReplay(1),
    );

    if (this.router) {
      this.router.events.pipe(
        takeUntil(this._destroyed$$),
        filter((e: RouterEvent) => e instanceof NavigationEnd),
        switchMap(() => this.mobile$),
      ).subscribe((isMobile: boolean) => {
        if (isMobile) {
          this.setOpen(false);
        }
      });
    }
  }

  setStatic(isStatic: boolean): void {
    this.static$$.next(isStatic);
    this.open$$.next(false);
  }

  setDisabled(isDisabled: boolean): void {
    this.disabled$$.next(isDisabled);
  }

  setOpen(isOpen: boolean): void {
    this.open$$.next(isOpen);
  }


  setShowMenuToggle(isVisible: boolean): void {
    this.forceShowMenuToggle$$.next(isVisible);
  }

  toggleOpen(): void {
    this.open$$.next(!this.open$$.getValue());
  }

  ngOnDestroy(): void {
    this._destroyed$$.next();
    this._destroyed$$.complete();
  }
}
